import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, Upload, Loader2, ArrowLeft, Check, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

// Toplu Stok Yönetimi — Excel export → preview → apply.
// Stok sayımı canonical `stocks.update_one` + `stock_movements(kind='sayim')` ile
// uygulanır (mevcut mekanizma REUSE). Ürün-level alanlar `products` üzerine $set.
// Depo değiştirme bu akışta engellidir (güvenli transfer gerekir).
const COUNT_LABELS = [
  ["eslesen", "Eşleşen", "text-emerald-400 border-emerald-500/30"],
  ["sayim", "Stok Sayımı", "text-cyan-300 border-cyan-500/30"],
  ["hatali", "Hatalı", "text-rose-400 border-rose-500/30"],
  ["eslesmeyen", "Eşleşmeyen", "text-orange-400 border-orange-500/30"],
];

export default function BulkStockDialog({ open, onOpenChange, onDone }) {
  const { can } = useAuth();
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const [mode, setMode] = useState("all");   // all/warehouse/kind/category/brand
  const [brands, setBrands] = useState([]);
  const [cats, setCats] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [brandSel, setBrandSel] = useState("");
  const [catSel, setCatSel] = useState("");
  const [whSel, setWhSel] = useState("");
  const [kindSel, setKindSel] = useState("");

  useEffect(() => {
    if (!open) return;
    setMode("all"); setBrandSel(""); setCatSel(""); setWhSel(""); setKindSel("");
    api.get("/products/brands").then(({ data }) => setBrands(data || [])).catch(() => {});
    api.get("/product-categories").then(({ data }) => setCats((data || []).filter((c) => c.active))).catch(() => {});
    api.get("/warehouses").then(({ data }) => setWarehouses((data || []).filter((w) => w.is_active !== false))).catch(() => {});
  }, [open]);

  const close = (v) => {
    onOpenChange?.(v);
    if (!v) { setPreview(null); setFile(null); }
  };

  const doExport = async () => {
    if (mode === "brand" && !brandSel) return toast.error("Marka seçin");
    if (mode === "category" && !catSel) return toast.error("Kategori seçin");
    if (mode === "warehouse" && !whSel) return toast.error("Depo seçin");
    if (mode === "kind" && !kindSel) return toast.error("Stok türü seçin");
    try {
      setBusy(true);
      const params = {};
      if (mode === "brand") params.brand = brandSel;
      if (mode === "category") params.category_id = catSel;
      if (mode === "warehouse") params.warehouse_id = whSel;
      if (mode === "kind") params.kind = kindSel;
      const resp = await api.get("/stocks/bulk/export", { responseType: "blob", params });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "toplu_stok.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Stok Excel şablonu indirildi");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doPreview = async (f) => {
    if (!f) return;
    try {
      setBusy(true);
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/stocks/bulk/preview", fd);
      setFile(f); setPreview(data);
      if (!data.degisecek) toast.info("Değişecek kayıt yok");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const doApply = async () => {
    if (!file) return;
    try {
      setBusy(true);
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/stocks/bulk/apply", fd);
      toast.success(`${data.applied_stock || 0} stok düzeltme hareketi uygulandı`);
      close(false);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const degisecek = preview?.degisecek || 0;
  const stokChanges = preview?.changes || [];       // sayım satırları
  const KINDS = [
    { v: "Üretim Ürünü", l: "Üretim Ürünü" },
    { v: "Ticari Ürün", l: "Ticari Ürün" },
    { v: "Kumaş", l: "Kumaş" },
    { v: "Aksesuar", l: "Aksesuar" },
  ];

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-4xl max-h-[92vh] flex flex-col p-0" data-testid="stock-bulk-dialog">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle className="text-slate-50 tracking-tight flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-cyan-400" /> Toplu Stok Yönetimi
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto flex-1 px-6 pb-6 space-y-4">
          {!preview && (
            <div className="space-y-4">
              <div className="rounded-sm border border-slate-800 bg-slate-950/40 p-4 space-y-2">
                <div className="text-xs text-slate-300 font-semibold">1. Excel şablonunu indir</div>
                <div className="flex flex-wrap gap-1.5" data-testid="stock-export-modes">
                  {[["all", "Tüm Stoklar"], ["warehouse", "Depoya Göre"], ["kind", "Stok Türüne Göre"],
                    ["category", "Kategoriye Göre"], ["brand", "Markaya Göre"]].map(([k, label]) => (
                    <button key={k} type="button" data-testid={`stock-mode-${k}`}
                      onClick={() => setMode(k)}
                      className={`px-2.5 py-1 text-[11px] rounded-sm border transition-colors ${
                        mode === k ? "border-cyan-500 bg-cyan-950/40 text-cyan-300"
                                   : "border-slate-700 bg-transparent text-slate-400 hover:border-slate-600"}`}>
                      {label}
                    </button>
                  ))}
                </div>
                {mode === "warehouse" && (
                  <select data-testid="stock-wh-select" value={whSel} onChange={(e) => setWhSel(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-700 rounded-sm h-8 text-xs text-slate-200 px-2">
                    <option value="">Depo seçin…</option>
                    {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                )}
                {mode === "kind" && (
                  <select data-testid="stock-kind-select" value={kindSel} onChange={(e) => setKindSel(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-700 rounded-sm h-8 text-xs text-slate-200 px-2">
                    <option value="">Stok türü seçin…</option>
                    {KINDS.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}
                  </select>
                )}
                {mode === "brand" && (
                  <select data-testid="stock-brand-select" value={brandSel} onChange={(e) => setBrandSel(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-700 rounded-sm h-8 text-xs text-slate-200 px-2">
                    <option value="">Marka seçin…</option>
                    {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                )}
                {mode === "category" && (
                  <select data-testid="stock-cat-select" value={catSel} onChange={(e) => setCatSel(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-700 rounded-sm h-8 text-xs text-slate-200 px-2">
                    <option value="">Kategori seçin…</option>
                    {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                )}
                <div className="text-[11px] text-slate-500">
                  Her satır bir stok satırıdır (ürün × varyant × depo). Varyantlı ürünlerde her beden ayrı satırdır; stok kaydı olmayan satırlar Mevcut Stok=0 gelir.
                  ID kolonları gizli referanstır. Yalnızca <b>Sayım Miktarı</b> kolonunu doldurun; Fark hücresi Excel formülüdür, DB'ye yazılmaz.
                </div>
                <Button onClick={doExport} disabled={busy} data-testid="stock-export-btn"
                  className="bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-sm text-xs uppercase tracking-wider">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-2" /> Excel'e Aktar</>}
                </Button>
              </div>
              <div className="rounded-sm border border-slate-800 bg-slate-950/40 p-4 space-y-2">
                <div className="text-xs text-slate-300 font-semibold">2. Excel'den güncelle / sayım yükle</div>
                <div className="text-[11px] text-slate-500">
                  Sayım miktarı farkı kadar <b>stok hareketi</b> oluşturulur (kim/ne zaman/eski/yeni/fark/depo audit'te korunur).
                  Boş sayım = stok değiştirme. Negatif sayım reddedilir. Depo değiştirilemez.
                  <br />Önce önizleme — onaylamadan hiçbir yazma yapılmaz.
                </div>
                <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="stock-upload-btn"
                  className="bg-cyan-700 hover:bg-cyan-600 rounded-sm text-xs uppercase tracking-wider">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-2" /> Excel'den Güncelle / Sayım Yükle</>}
                </Button>
                <input ref={fileRef} type="file" accept=".xlsx" className="hidden"
                  data-testid="stock-file-input" onChange={(e) => doPreview(e.target.files?.[0])} />
              </div>
            </div>
          )}

          {preview && (
            <div className="space-y-4" data-testid="stock-bulk-preview">
              <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                {COUNT_LABELS.map(([key, label, cls]) => (
                  <div key={key} className={`rounded-sm border bg-slate-950/40 px-2 py-1.5 text-center ${cls}`} data-testid={`stock-count-${key}`}>
                    <div className="text-lg font-bold font-mono">{preview[key] ?? 0}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
                  </div>
                ))}
              </div>

              <div className="text-xs text-slate-400">
                <span className="text-cyan-300 font-mono font-semibold" data-testid="stock-degisecek">{degisecek}</span> değişiklik uygulanacak
              </div>

              {!!stokChanges.length && (
                <div className="border border-slate-800 rounded-sm overflow-hidden">
                  <div className="px-3 py-1.5 bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-400 font-mono">Stok Sayımı</div>
                  <div className="max-h-56 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="hover:bg-transparent border-slate-800">
                          <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Ürün</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Depo</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Varyant</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Mevcut</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Sayım</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Fark</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {stokChanges.map((c, i) => (
                          <TableRow key={i} className="border-slate-800" data-testid={`stock-change-row-${i}`}>
                            <TableCell className="text-xs text-slate-300">{c.name}</TableCell>
                            <TableCell className="text-xs text-slate-500">{c.warehouse}</TableCell>
                            <TableCell className="text-xs text-slate-500">{c.variant_sku || "—"}</TableCell>
                            <TableCell className="text-xs text-slate-400">{c.old}</TableCell>
                            <TableCell className="text-xs text-cyan-300 font-semibold">{c.new}</TableCell>
                            <TableCell className={`text-xs font-semibold ${c.delta > 0 ? "text-emerald-400" : "text-rose-400"}`}>
                              {c.delta > 0 ? "+" : ""}{c.delta}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {!stokChanges.length && (
                <div className="rounded-sm border border-slate-800 bg-slate-950/40 p-6 text-center text-xs text-slate-500">
                  Değişiklik yok
                </div>
              )}

              {!!(preview.error_rows || []).length && (
                <div className="rounded-sm border border-rose-900/50 bg-rose-950/20 p-3" data-testid="stock-errors">
                  <div className="text-[11px] font-semibold text-rose-300 mb-1">Hatalı satırlar ({preview.error_rows.length})</div>
                  <div className="max-h-28 overflow-y-auto space-y-0.5">
                    {preview.error_rows.map((e, i) => (
                      <div key={i} className="text-[10px] font-mono text-rose-200/80">Satır {e.row}: {e.reason}</div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pt-1">
                <Button variant="outline" onClick={() => { setPreview(null); setFile(null); }} disabled={busy}
                  data-testid="stock-back-btn"
                  className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm text-xs">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Geri
                </Button>
                <Button onClick={doApply} disabled={(busy || !degisecek || !!(preview.error_rows || []).length) || !(can("warehouse", "edit"))} title={!(can("warehouse", "edit")) ? "Toplu stok yükleme yetkiniz yok (warehouse.edit)" : undefined}
                  data-testid="stock-apply-btn"
                  className="bg-emerald-600 hover:bg-emerald-500 rounded-sm text-xs uppercase tracking-wider font-semibold">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" /> Onayla ve Uygula ({degisecek})</>}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
